import type { ApiError, SignInParams } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { AnnouncementList } from '~/entities/announcements'
import type { ViewError } from './index.h'

import { is, Logic } from '@bldsoft/cappie/rest'
import { api } from '@setplex/tria-api'
import { combine, merge, createEvent, createStore, sample } from 'effector'
import { persist } from 'effector-storage/local'
import { delay, reset } from 'patronum'
import * as v from 'valibot'
import { features } from '~/core/flags'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import {
  AnnouncementListSchema,
  formatAnnouncementText,
} from '~/entities/announcements'
import { model as sessionModel } from '~/entities/session'
import { lookbehind, noop } from '~/shared/effector'
import { getOnlyStoresFromApiDomains } from '~/shared/effector/getOnlyStoresFromApiDomains'
import { aye, F, nay, T } from '~/shared/helpers'
import { signIn, signInFx, signOut, signOutFx } from './common'
import {
  $isTrueIdAuthed,
  $trueIdError,
  trueIdLogoutFx,
} from './sign-in-by-trueId/model'

export { signIn, signInFx, signOut, signOutFx } from './common'

export const toggleRememberMe = createEvent()

export const $rememberMe = createStore<boolean>(false).on(
  toggleRememberMe,
  (state) => !state
)

// flag, indicating explicit sign out (= not authentication lost due to session expiration)
export const $explicit = createStore<boolean>(false)
  .on(signOutFx, T)
  .on(delay({ source: signOutFx.finally, timeout: 10 }), F)

// debounced error, in order to prevent react to render component with old data
export const $viewError = createStore<ViewError>(null)

// Transformed error state into a displayable message with translation
export const $message = combine($viewError, i18n.$t, (error, { t }) => ({
  show: Boolean(error),
  title: error ? t(error) : undefined,
}))

// Transformed trueId error state into a displayable message with translation
export const $trueIdmessage = combine(
  $viewError,
  $trueIdError,
  i18n.$t,
  (error, localError, { t }) => {
    const finalError = error || localError

    return {
      show: Boolean(finalError),
      title: finalError ? t(finalError) : undefined,
    }
  }
)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $rememberMe,
  key: 'tria__remember-me',
  fail: noop,
})

sample({
  clock: signIn,
  target: signInFx,
})

//
// Handle failed sign-in messages
// https://setplexapps.atlassian.net/browse/FP-3969
// https://setplexapps.atlassian.net/wiki/spaces/NORA/pages/3026223105/CR-319.+Error+Messages+improvement
//

type SubscriptionErrorMessage = {
  header?: string
  subject: string
  message: string
}

const failedSignIn = createEvent<ApiError>()
export const showSubscriptionErrorMessage = createEvent<SubscriptionErrorMessage>() // prettier-ignore
export const $showSubscriptionErrorMessage = createStore<(SignInParams & SubscriptionErrorMessage) | null>(null) // prettier-ignore

lookbehind({
  source: signInFx.failData,
  after: merge([
    api.events.logic.subscriptionHasBeenExpired,
    api.events.logic.accountOrDeviceHasBeenSuspended,
    api.events.logic.subscriptionIsApprovalPending,
  ]),
  within: 100,
  target: failedSignIn,
})

sample({
  clock: failedSignIn,
  filter(error) {
    return (
      is(error, Logic) &&
      v.is(AnnouncementListSchema, error.payload) &&
      error.payload.announcements.length === 1
    )
  },
  fn(error) {
    const list = (
      is(error, Logic) ? error.payload : undefined
    ) as AnnouncementList
    const announcement = list.announcements[0]
    const header = announcement.type
    const message = announcement.message
    const subject = announcement.subject
    return {
      header,
      subject: formatAnnouncementText(subject ?? '', list.tokens),
      message: formatAnnouncementText(message ?? '', list.tokens),
    }
  },
  target: showSubscriptionErrorMessage,
})

sample({
  clock: showSubscriptionErrorMessage,
  source: signInFx.fail,
  fn: ({ params }, msg) => ({ ...params, ...msg }),
  target: $showSubscriptionErrorMessage,
})

sample({
  clock: showSubscriptionErrorMessage,
  fn: () => '/subscription-status',
  target: router.navigatePush,
})

//
//
//

sample({
  clock: signOut,
  source: $isTrueIdAuthed,
  filter: (isTrueIdAuthed) => nay(isTrueIdAuthed),
  fn: () => undefined,
  target: signOutFx,
})

sample({
  clock: signOut,
  source: $isTrueIdAuthed,
  filter: (isTrueIdAuthed) => aye(isTrueIdAuthed),
  target: trueIdLogoutFx,
})

sample({
  clock: signOutFx,
  source: features.get('guestModeEnabled', false),
  filter: Boolean,
  target: sessionModel.startGuestReloginTransition,
})

sample({
  clock: [signOutFx.fail, signInFx.finally, api.events.logic.guestModeDisabled],
  target: sessionModel.stopGuestReloginTransition,
})

// cleanup stores after sign-out
reset({
  clock: signOutFx.finally,
  target: getOnlyStoresFromApiDomains([
    api.subscriber,
    api.player,
    api.vods,
    api.tv,
    api.tvshows,
    api.banners,
    api.liveEvents,
    api.tvBundles,
    api.showBundles,
    api.vodBundles,
  ]),
})

// indicates about enabled or disabled registration by phone
export const $signInWithPhoneEnabled: Store<boolean> = features.get(
  'signInWithPhoneEnabled',
  false
) // `false` by default

//
// register stores and events
//

register(signIn, 'feature/subscriber/sign-in/signIn')
register(signOut, 'feature/subscriber/sign-in/signOut')
